import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useUrlState } from '../hooks/useUrlState';
import {
  api,
  CATCH_ALL_DEAL_TYPE,
  type CommissionDealType,
  type CommissionExceptionKind,
  type CommissionOverrideInput,
  type CommissionRateRow,
  type CommissionRateYear,
} from '../lib/api';
import {
  ActionButton, Badge, Card, CardHeader, EmptyState, SectionLabel, Select, Spinner, Table, Td, Th, Tr,
} from '../components/ui';
import { fmtCurrencyWhole } from '../lib/fmt';

const currentYear = () => new Date().getFullYear();

const inputStyle: React.CSSProperties = {
  width: 78, textAlign: 'right', fontSize: 12, padding: '3px 5px',
  border: '0.5px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', color: 'var(--color-text)',
};

const hint: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)' };

/** Keep only digits and a single decimal point — same guard the quota grid uses. */
const numeric = (v: string) => v.replace(/[^0-9.]/g, '');

type Draft = Record<string, { rate: string; duration: string }>;

const emptyCell = { rate: '', duration: '' };

function dealTypeLabel(t: CommissionDealType) {
  return t.value === CATCH_ALL_DEAL_TYPE ? t.label : `${t.label}`;
}

// ─── Base schedule ───────────────────────────────────────────────────────────

function ScheduleCard({
  year, data, onChanged,
}: {
  year: number;
  data: CommissionRateYear;
  onChanged: () => void;
}) {
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const seed: Draft = {};
    for (const t of data.deal_types) {
      const row = data.rates.find((r) => r.deal_type === t.value);
      seed[t.value] = row
        ? { rate: String(row.rate_pct), duration: String(row.duration_months) }
        : { ...emptyCell };
    }
    setDraft(seed);
    setMsg(null);
  }, [data]);

  const active = data.rates[0];
  const inherited = active?.is_inherited === true;
  const configured = data.rates.length > 0;

  const set = (dealType: string, field: 'rate' | 'duration', v: string) => {
    setDraft((prev) => ({ ...prev, [dealType]: { ...(prev[dealType] ?? emptyCell), [field]: numeric(v) } }));
    setMsg(null);
  };

  async function save() {
    const rows: CommissionRateRow[] = [];
    for (const t of data.deal_types) {
      const cell = draft[t.value] ?? emptyCell;
      if (cell.rate.trim() === '') continue;            // blank rate = no rate for this type
      const rate = Number(cell.rate);
      const duration = Number(cell.duration);
      if (!Number.isFinite(rate)) { setMsg(`${dealTypeLabel(t)}: rate is not a number.`); return; }
      if (cell.duration.trim() === '' || !Number.isInteger(duration) || duration < 1) {
        setMsg(`${dealTypeLabel(t)}: enter how many months we pay for.`);
        return;
      }
      rows.push({ deal_type: t.value, rate_pct: rate, duration_months: duration });
    }
    setSaving(true);
    try {
      const { saved } = await api.admin.commissionRates.save(`${year}-01-01`, rows);
      setMsg(`Saved ${saved} rate${saved === 1 ? '' : 's'} for ${year}.`);
      onChanged();
    } catch (e) {
      setMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function clearYear() {
    if (!confirm(`Delete the ${year} commission rates? Deals closing in ${year} will fall back to whatever earlier version is in effect.`)) return;
    try {
      await api.admin.commissionRates.delete(`${year}-01-01`);
      onChanged();
    } catch (e) {
      setMsg(`Delete failed: ${(e as Error).message}`);
    }
  }

  const hasOwnYearVersion = configured && !inherited;

  return (
    <Card style={{ marginBottom: 14 }}>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Rate schedule</span>
        <span style={{ marginLeft: 8 }}>
          {!configured
            ? <Badge variant="red">Not configured</Badge>
            : inherited
              ? <Badge variant="amber">Inherited from {active?.effective_from.slice(0, 4)}</Badge>
              : <Badge variant="green">Set for {year}</Badge>}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <ActionButton variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : `Save ${year} rates`}
          </ActionButton>
          {hasOwnYearVersion && <ActionButton variant="danger" onClick={clearYear}>Delete {year}</ActionButton>}
        </div>
      </CardHeader>

      <div style={{ padding: '8px 16px', ...hint }}>
        A deal is priced by the schedule in force on its <strong>close date</strong> and keeps that
        rate for life — editing {year} never re-prices a deal that closed earlier. The percentage is
        the <strong>total</strong> paid on the deal; where HubSpot records a deal split it is
        apportioned across the split owners. Commission is calculated on invoiced services only,
        excluding fees. Leave a rate blank to pay nothing on that deal type.
      </div>

      {inherited && (
        <div style={{ padding: '0 16px 8px', ...hint }}>
          These values are carried forward from {active?.effective_from.slice(0, 4)}. Saving creates
          a {year} version; deals that closed before {year} keep the old one.
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead><tr>
            <Th sticky>Deal type</Th>
            <Th right>Rate %</Th>
            <Th right>Pay for (months)</Th>
            <Th right>Closed-won deals</Th>
          </tr></thead>
          <tbody>
            {data.deal_types.map((t) => (
              <Tr key={t.value}>
                <Td bold>
                  {dealTypeLabel(t)}
                  {t.value === CATCH_ALL_DEAL_TYPE && (
                    <span style={{ ...hint, marginLeft: 6 }}>fallback for anything not listed above</span>
                  )}
                </Td>
                <Td right>
                  <input
                    style={inputStyle}
                    inputMode="decimal"
                    placeholder="—"
                    value={draft[t.value]?.rate ?? ''}
                    onChange={(e) => set(t.value, 'rate', e.target.value)}
                  />
                </Td>
                <Td right>
                  <input
                    style={inputStyle}
                    inputMode="numeric"
                    placeholder="12"
                    value={draft[t.value]?.duration ?? ''}
                    onChange={(e) => set(t.value, 'duration', e.target.value)}
                  />
                </Td>
                <Td right>{t.deal_count}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>
      {msg && <div style={{ padding: '6px 16px', ...hint }}>{msg}</div>}
    </Card>
  );
}

// ─── Per-rep overrides ───────────────────────────────────────────────────────

function OverridesCard({
  year, data, onChanged,
}: {
  year: number;
  data: CommissionRateYear;
  onChanged: () => void;
}) {
  const [urlRep, setUrlRep] = useUrlState('rep', '');
  const selectedRep = urlRep || data.reps[0]?.hubspot_owner_id || '';
  const [draft, setDraft] = useState<Draft>({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const repOverrides = useMemo(
    () => data.overrides.find((o) => o.hubspot_owner_id === selectedRep),
    [data, selectedRep],
  );

  useEffect(() => {
    const seed: Draft = {};
    for (const t of data.deal_types) {
      const row = repOverrides?.rates.find((r) => r.deal_type === t.value);
      seed[t.value] = row
        ? { rate: String(row.rate_pct), duration: row.duration_months == null ? '' : String(row.duration_months) }
        : { ...emptyCell };
    }
    setDraft(seed);
    setMsg(null);
  }, [data, repOverrides, selectedRep]);

  const set = (dealType: string, field: 'rate' | 'duration', v: string) => {
    setDraft((prev) => ({ ...prev, [dealType]: { ...(prev[dealType] ?? emptyCell), [field]: numeric(v) } }));
    setMsg(null);
  };

  /** The base rate this rep would get without an override — shown as the input
   *  placeholder so it's obvious what a blank cell means. */
  const baseFor = (dealType: string) => data.rates.find((r) => r.deal_type === dealType);

  async function save() {
    const rows: CommissionOverrideInput[] = [];
    for (const t of data.deal_types) {
      const cell = draft[t.value] ?? emptyCell;
      if (cell.rate.trim() === '') continue;            // blank = no override, inherit the schedule
      const rate = Number(cell.rate);
      if (!Number.isFinite(rate)) { setMsg(`${dealTypeLabel(t)}: rate is not a number.`); return; }
      const durationRaw = cell.duration.trim();
      const duration = durationRaw === '' ? null : Number(durationRaw);
      if (duration != null && (!Number.isInteger(duration) || duration < 1)) {
        setMsg(`${dealTypeLabel(t)}: months must be a whole number, or blank to inherit.`);
        return;
      }
      rows.push({ deal_type: t.value, rate_pct: rate, duration_months: duration });
    }
    setSaving(true);
    try {
      if (rows.length === 0) {
        // An empty grid means "this rep has no overrides" — delete the version
        // rather than writing zero rows, so they cleanly fall back to the schedule.
        await api.admin.commissionRates.deleteOverrides(selectedRep, `${year}-01-01`);
        setMsg(`Cleared ${year} overrides.`);
      } else {
        const { saved } = await api.admin.commissionRates.saveOverrides(selectedRep, `${year}-01-01`, rows);
        setMsg(`Saved ${saved} override${saved === 1 ? '' : 's'} for ${year}.`);
      }
      onChanged();
    } catch (e) {
      setMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  if (data.reps.length === 0) {
    return <Card><EmptyState message="No sales reps yet — reps appear once they own or share a HubSpot deal." /></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Per-rep overrides</span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Select value={selectedRep} onChange={setUrlRep}>
            {data.reps.map((r) => (
              <option key={r.hubspot_owner_id} value={r.hubspot_owner_id}>
                {r.owner_name ?? r.hubspot_owner_id}{r.archived ? ' (former)' : ''}
              </option>
            ))}
          </Select>
          <ActionButton variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save overrides'}
          </ActionButton>
        </div>
      </CardHeader>

      <div style={{ padding: '8px 16px', ...hint }}>
        An override changes <strong>only this rep's share</strong> — everyone else on the deal keeps
        the schedule rate, so the deal's total payout flexes. Leave a rate blank to use the schedule.
        Leave months blank to keep the schedule's payout window and change only the percentage.
      </div>

      <div style={{ overflowX: 'auto' }}>
        <Table>
          <thead><tr>
            <Th sticky>Deal type</Th>
            <Th right>Schedule</Th>
            <Th right>Override %</Th>
            <Th right>Pay for (months)</Th>
          </tr></thead>
          <tbody>
            {data.deal_types.map((t) => {
              const base = baseFor(t.value);
              return (
                <Tr key={t.value}>
                  <Td bold>{dealTypeLabel(t)}</Td>
                  <Td right>
                    {base ? `${base.rate_pct}% · ${base.duration_months}m` : <span style={hint}>not set</span>}
                  </Td>
                  <Td right>
                    <input
                      style={inputStyle}
                      inputMode="decimal"
                      placeholder={base ? String(base.rate_pct) : '—'}
                      value={draft[t.value]?.rate ?? ''}
                      onChange={(e) => set(t.value, 'rate', e.target.value)}
                    />
                  </Td>
                  <Td right>
                    <input
                      style={inputStyle}
                      inputMode="numeric"
                      placeholder={base ? String(base.duration_months) : '—'}
                      value={draft[t.value]?.duration ?? ''}
                      onChange={(e) => set(t.value, 'duration', e.target.value)}
                    />
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {msg && <div style={{ padding: '6px 16px', ...hint }}>{msg}</div>}
    </Card>
  );
}

// ─── Exceptions ──────────────────────────────────────────────────────────────

const EXCEPTION_LABEL: Record<CommissionExceptionKind, string> = {
  ambiguous_account: 'Account linked to two deals',
  deposit_credit_after_lock: 'Deposit drawn down after lock',
  deposit_credit_unverified: 'Deposit source unconfirmed',
  missing_services_amount: 'Invoice detail missing',
  no_rate: 'No rate for this deal',
  no_owner: 'Nobody to pay',
  split_sum_mismatch: 'Split ≠ 100%',
};

const EXCEPTION_VARIANT: Record<CommissionExceptionKind, 'red' | 'amber'> = {
  ambiguous_account: 'red',
  deposit_credit_after_lock: 'red',
  deposit_credit_unverified: 'amber',
  missing_services_amount: 'amber',
  no_rate: 'red',
  no_owner: 'red',
  split_sum_mismatch: 'amber',
};

function ExceptionsCard() {
  const exceptionsQuery = useQuery(() => api.admin.commissions.exceptions(), []);
  const [busy, setBusy] = useState<null | 'recompute' | 'backfill'>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const exceptions = exceptionsQuery.data?.exceptions ?? [];
  // Two different kinds of wrong, so two running totals — adding them together
  // would net an underpayment against an overpayment and report neither.
  const sumOf = (direction: 'unpaid' | 'overpaid') => exceptions
    .filter((e) => (e.direction ?? 'unpaid') === direction)
    .reduce((sum, e) => sum + (e.amount_at_risk ?? 0), 0);
  const atRisk = sumOf('unpaid');
  const overpaid = sumOf('overpaid');

  async function recompute() {
    setBusy('recompute');
    setMsg(null);
    try {
      const r = await api.admin.commissions.recompute();
      setMsg(`Rebuilt ${r.rows_written} payout row${r.rows_written === 1 ? '' : 's'} across ${r.periods_written} open period${r.periods_written === 1 ? '' : 's'}. ${r.periods_locked} locked period${r.periods_locked === 1 ? '' : 's'} left untouched.`);
      exceptionsQuery.refetch();
    } catch (e) {
      setMsg(`Recompute failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  async function backfill() {
    setBusy('backfill');
    setMsg(null);
    try {
      const r = await api.admin.commissions.backfillInvoiceDetail();
      setMsg(
        `Fetched detail for ${r.scanned} invoice${r.scanned === 1 ? '' : 's'}: ${r.updated} filled in, ${r.unchanged} had no line items, ${r.errored} errored. ${r.remaining} still missing — run again to continue.`,
      );
      exceptionsQuery.refetch();
    } catch (e) {
      setMsg(`Backfill failed: ${(e as Error).message}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card style={{ marginBottom: 14 }}>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Exceptions</span>
        <span style={{ marginLeft: 8 }}>
          {exceptions.length === 0
            ? <Badge variant="green">All clear</Badge>
            : <Badge variant="red">{exceptions.length} to fix</Badge>}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <ActionButton onClick={backfill} disabled={busy !== null}>
            {busy === 'backfill' ? 'Fetching…' : 'Backfill invoice detail'}
          </ActionButton>
          <ActionButton variant="primary" onClick={recompute} disabled={busy !== null}>
            {busy === 'recompute' ? 'Recomputing…' : 'Recompute now'}
          </ActionButton>
        </div>
      </CardHeader>

      <div style={{ padding: '8px 16px', ...hint }}>
        Deals and invoices that can't be paid out until someone fixes them, plus anything already
        paid that shouldn't have been. Commissions rebuild on every data refresh; recompute here
        after editing rates rather than waiting for the next run. Locked periods are never rebuilt,
        so a correction to one is reported here rather than applied.
      </div>

      {exceptionsQuery.error ? (
        <EmptyState message={exceptionsQuery.error} />
      ) : exceptionsQuery.isLoading ? (
        <Spinner />
      ) : exceptions.length === 0 ? (
        <EmptyState message="Nothing blocked — every commissionable invoice resolved to a deal, a rate and a payee." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr>
              <Th sticky>Problem</Th>
              <Th>Subject</Th>
              <Th>What to do</Th>
              <Th right>Amount</Th>
            </tr></thead>
            <tbody>
              {exceptions.map((e, i) => (
                <Tr key={`${e.kind}-${e.subject}-${i}`}>
                  <Td><Badge variant={EXCEPTION_VARIANT[e.kind]}>{EXCEPTION_LABEL[e.kind]}</Badge></Td>
                  <Td bold>{e.subject}</Td>
                  <Td>{e.detail}</Td>
                  <Td right>
                    {e.amount_at_risk == null ? '—' : (
                      <>
                        {fmtCurrencyWhole(e.amount_at_risk)}
                        {(e.direction ?? 'unpaid') === 'overpaid' && (
                          <Badge variant="red">overpaid</Badge>
                        )}
                      </>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {(msg || atRisk > 0 || overpaid > 0) && (
        <div style={{ padding: '6px 16px', ...hint }}>
          {msg ?? [
            atRisk > 0 ? `${fmtCurrencyWhole(atRisk)} of invoiced services is currently earning no commission.` : null,
            overpaid > 0 ? `${fmtCurrencyWhole(overpaid)} of commission was paid on money that had already been commissioned as a deposit — net it against the next payment run.` : null,
          ].filter(Boolean).join(' ')}
        </div>
      )}
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdminCommissionsPage() {
  const [yearStr, setYearStr] = useUrlState('year', String(currentYear()));
  const year = parseInt(yearStr) || currentYear();

  const ratesQuery = useQuery(() => api.admin.commissionRates.year(year), [year]);
  const data = ratesQuery.data;

  const yearOptions = useMemo(() => {
    const cy = currentYear();
    const years: number[] = [];
    for (let y = cy - 2; y <= cy + 2; y++) years.push(y);
    return years;
  }, []);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Sales commissions</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Deals closing in</span>
          <Select value={String(year)} onChange={(v) => setYearStr(v)}>
            {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
          </Select>
        </span>
      </div>

      {ratesQuery.error ? (
        <EmptyState message={ratesQuery.error} />
      ) : ratesQuery.isLoading || !data ? (
        <Spinner />
      ) : (
        <>
          <ExceptionsCard />
          <ScheduleCard year={year} data={data} onChanged={ratesQuery.refetch} />
          <SectionLabel>Per-rep overrides — {year}</SectionLabel>
          <OverridesCard year={year} data={data} onChanged={ratesQuery.refetch} />
        </>
      )}
    </>
  );
}
